<template>
  <div class="ocr-page-section">
    <OcrPageShimmer v-if="loading" variant="test-data" />
    <OcrApiErrorPanel
      v-else-if="loadError"
      :message="loadError"
      :retrying="loading"
      @retry="loadOverview"
    />
    <div v-else class="ocr-tabbed-stack">
      <p class="ocr-panel-lead ocr-test-caption">
        Review, merge, and upload processed sample and test data to the database.
      </p>
      <OcrTabs v-model="activeTab">
        <v-tab value="reports">Report Files</v-tab>
        <v-tab value="merged">Merged Files</v-tab>
        <v-tab value="source">Tenant Source Files</v-tab>
      </OcrTabs>

      <v-window v-model="activeTab" class="ocr-tab-window">
        <v-window-item value="reports">
          <div class="ocr-toolbar ocr-toolbar--stack">
            <div class="ocr-toolbar-row">
              <div class="ocr-field ocr-test-type-select">
                <label class="profile-menu-label">Test type</label>
                <v-select
                  v-model="reportTestType"
                  :items="testTypes"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </div>
              <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" :loading="uploadingCsv" @click="$refs.reportCsvInput.click()">
                Upload CSV
              </v-btn>
              <input
                ref="reportCsvInput"
                type="file"
                accept=".csv"
                multiple
                hidden
                @change="uploadCsv($event, false)"
              />
            </div>
          </div>

          <p v-if="!reportSummary.length" class="ocr-empty-cell">No report CSV files in the output folder.</p>
          <OcrTableWrap v-else title="Reports">
            <v-table class="well-table sample-table well-table-data fd-table ocr-sticky-table">
              <thead>
                <tr class="main-header">
                  <th class="ocr-select-col ocr-sticky-col"></th>
                  <th
                    v-for="col in reportSummaryColumns"
                    :key="col"
                    :class="{ 'ocr-sticky-col ocr-sticky-col--name': col === 'Report' }"
                    :title="col"
                  >{{ col }}</th>
                </tr>
              </thead>
              <tbody class="well-table-content">
                <tr
                  v-for="row in reportSummary"
                  :key="row.Report"
                  class="ocr-table-pick-row"
                  :class="{ 'ocr-table-pick-row--active': selectedReports.includes(row.Report) }"
                  @click="toggleReport(row.Report)"
                >
                  <td class="ocr-select-col ocr-sticky-col">
                    <v-checkbox
                      :model-value="selectedReports.includes(row.Report)"
                      hide-details
                      density="compact"
                      @click.stop
                      @update:model-value="toggleReport(row.Report)"
                    />
                  </td>
                  <td
                    v-for="col in reportSummaryColumns"
                    :key="col"
                    :class="{ 'ocr-sticky-col ocr-sticky-col--name': col === 'Report' }"
                  >
                    <span
                      :class="{ 'ocr-table-cell': col === 'Report' }"
                      :title="col === 'Report' ? String(row[col] ?? '') : undefined"
                    >{{ row[col] }}</span>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </OcrTableWrap>

          <v-expansion-panels v-if="selectedReports.length" multiple class="ocr-expanders">
            <v-expansion-panel v-for="report in selectedReports" :key="report">
              <v-expansion-panel-title>{{ report }}</v-expansion-panel-title>
              <v-expansion-panel-text>
                <div
                  v-for="file in filesForReport(report)"
                  :key="file.file_name"
                  class="ocr-test-file-block"
                >
                  <div class="ocr-test-file-head">
                    <span class="ocr-file-heading">{{ fileHeading(file) }}</span>
                    <div class="ocr-test-file-actions">
                      <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" @click="goToReport(file)">GO</v-btn>
                      <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" @click="downloadCsv(file.file_name)">Download</v-btn>
                    </div>
                  </div>
                  <OcrEditableGrid
                    v-if="csvGrids[file.file_name]"
                    :grid="csvGrids[file.file_name]"
                    :saving="savingCsv === file.file_name"
                    @save="saveCsv(file.file_name, $event)"
                  />
                  <p v-else-if="loadingCsv === file.file_name" class="ocr-empty-cell">Loading…</p>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-window-item>

        <v-window-item value="merged">
          <div class="ocr-toolbar ocr-toolbar--stack">
            <div class="ocr-toolbar-row">
              <div class="ocr-field ocr-test-type-select">
                <label class="profile-menu-label">Test type</label>
                <v-select
                  v-model="mergedTestType"
                  :items="testTypes"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </div>
              <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="merging" @click="mergeFiles">
                Merge
              </v-btn>
              <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" @click="viewMerged = !viewMerged">
                {{ viewMerged ? "Hide merged files" : "View merged files" }}
              </v-btn>
            </div>
            <div class="ocr-toolbar-row">
              <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="uploadingAll" @click="uploadAll">
                Upload all to database
              </v-btn>
              <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" :loading="uploadingCsv" @click="$refs.mergedCsvInput.click()">
                Upload CSV
              </v-btn>
              <input
                ref="mergedCsvInput"
                type="file"
                accept=".csv"
                multiple
                hidden
                @change="uploadCsv($event, true)"
              />
              <v-btn size="small" color="error" variant="outlined" class="ocr-toolbar-btn" :disabled="!mergedFiles.length" @click="deleteAllMerged">
                Delete merged
              </v-btn>
              <v-btn size="small" color="error" variant="text" class="ocr-toolbar-btn" @click="confirmDeleteSamples = true">
                Delete samples
              </v-btn>
            </div>
          </div>

          <p v-if="!mergedFiles.length" class="ocr-empty-cell">No merged files. Run merge first.</p>
          <v-expansion-panels v-else-if="viewMerged" multiple class="ocr-expanders">
            <v-expansion-panel v-for="file in visibleMergedFiles" :key="file">
              <v-expansion-panel-title>{{ mergedTableType(file) }}</v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="ocr-test-file-head ocr-test-file-head--stack">
                  <div class="ocr-test-merged-selects">
                    <div class="ocr-field">
                      <label class="profile-menu-label">Table type</label>
                      <v-select
                        v-model="mergedConfigs[file].tableType"
                        :items="tableTypeNames"
                        density="compact"
                        variant="outlined"
                        hide-details
                        @update:model-value="onMergedTypeChange(file)"
                      />
                    </div>
                    <div class="ocr-field">
                      <label class="profile-menu-label">Display name</label>
                      <v-select
                        v-model="mergedConfigs[file].displayName"
                        :items="displayNamesFor(mergedConfigs[file].tableType)"
                        density="compact"
                        variant="outlined"
                        hide-details
                      />
                    </div>
                  </div>
                  <div class="ocr-test-file-actions">
                    <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" @click="downloadCsv(file)">Download</v-btn>
                    <v-btn size="small" color="error" variant="text" class="ocr-toolbar-btn" @click="deleteMerged(file)">Delete</v-btn>
                    <v-btn
                      size="small"
                      color="primary"
                      variant="flat"
                      class="ocr-toolbar-btn"
                      :loading="uploadingFile === file"
                      @click="uploadMerged(file)"
                    >
                      Upload to database
                    </v-btn>
                  </div>
                </div>
                <pre v-if="jsonPreview[file]" class="ocr-test-json">{{ jsonPreview[file] }}</pre>
                <OcrEditableGrid
                  v-if="csvGrids[file]"
                  :grid="csvGrids[file]"
                  :saving="savingCsv === file"
                  @save="saveCsv(file, $event)"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-window-item>

        <v-window-item value="source">
          <div class="ocr-toolbar ocr-toolbar--stack">
            <div class="ocr-toolbar-row">
              <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" :loading="uploadingSource" @click="$refs.sourceInput.click()">
                Upload files
              </v-btn>
              <input
                ref="sourceInput"
                type="file"
                accept=".pdf,.png,.xlsx,.xls"
                multiple
                hidden
                @change="uploadSource"
              />
              <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" @click="completedDialog = true">
                From completed
              </v-btn>
              <v-btn size="small" color="error" variant="outlined" class="ocr-toolbar-btn" :disabled="!selectedSource.length" @click="deleteSource">
                Delete selected
              </v-btn>
            </div>
          </div>
          <OcrTableWrap title="Tenant source files">
            <p v-if="!sourceFiles.length" class="ocr-empty-cell ocr-empty-cell--padded">No source files.</p>
            <v-table v-else class="well-table sample-table well-table-data fd-table">
              <thead>
                <tr class="main-header">
                  <th class="ocr-select-col"></th>
                  <th>File name</th>
                </tr>
              </thead>
              <tbody class="well-table-content">
                <tr
                  v-for="name in sourceFiles"
                  :key="name"
                  class="ocr-table-pick-row"
                  :class="{ 'ocr-table-pick-row--active': selectedSource.includes(name) }"
                  @click="toggleSource(name)"
                >
                  <td class="ocr-select-col">
                    <v-checkbox
                      :model-value="selectedSource.includes(name)"
                      hide-details
                      density="compact"
                      @click.stop
                      @update:model-value="toggleSource(name)"
                    />
                  </td>
                  <td><span class="ocr-table-cell">{{ name }}</span></td>
                </tr>
              </tbody>
            </v-table>
          </OcrTableWrap>
        </v-window-item>
      </v-window>
    </div>

    <v-dialog v-model="completedDialog" max-width="640">
      <v-card class="ocr-dialog-card">
        <v-card-title class="ocr-dialog-title">Upload from completed files</v-card-title>
        <v-card-text>
          <p v-if="!completedFiles.length" class="ocr-empty-cell">No completed files.</p>
          <OcrTableWrap v-else>
            <v-table class="well-table sample-table well-table-data fd-table">
              <thead>
                <tr class="main-header">
                  <th class="ocr-select-col"></th>
                  <th>File name</th>
                </tr>
              </thead>
              <tbody class="well-table-content">
                <tr
                  v-for="name in completedFiles"
                  :key="name"
                  class="ocr-table-pick-row"
                  :class="{ 'ocr-table-pick-row--active': selectedCompleted.includes(name) }"
                  @click="toggleCompleted(name)"
                >
                  <td class="ocr-select-col">
                    <v-checkbox :model-value="selectedCompleted.includes(name)" hide-details density="compact" />
                  </td>
                  <td><span class="ocr-table-cell">{{ name }}</span></td>
                </tr>
              </tbody>
            </v-table>
          </OcrTableWrap>
        </v-card-text>
        <v-card-actions class="ocr-dialog-actions">
          <v-spacer />
          <v-btn variant="text" class="ocr-toolbar-btn" @click="completedDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" class="ocr-toolbar-btn" :disabled="!selectedCompleted.length" :loading="copyingCompleted" @click="copyCompleted">
            Upload selected
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDeleteSamples" max-width="480">
      <v-card class="ocr-dialog-card">
        <v-card-title class="ocr-dialog-title">Delete all samples</v-card-title>
        <v-card-text>
          This deletes every sample for the current tenant from the FluidsData API.
        </v-card-text>
        <v-card-actions class="ocr-dialog-actions">
          <v-spacer />
          <v-btn variant="text" class="ocr-toolbar-btn" @click="confirmDeleteSamples = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" class="ocr-toolbar-btn" :loading="deletingSamples" @click="deleteAllSamples">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import api from "@/axiosInterceptor";
import OcrTabs from "@/components/layout/OCR/OcrTabs.vue";
import OcrEditableGrid from "@/components/layout/OCR/OcrEditableGrid.vue";
import OcrPageShimmer from "@/components/layout/OCR/OcrPageShimmer.vue";
import OcrApiErrorPanel from "@/components/layout/OCR/OcrApiErrorPanel.vue";
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";
import { ocrNotifyMixin } from "@/components/layout/OCR/OcrSnackbar.vue";

export default {
  name: "OcrTestData",
  mixins: [ocrNotifyMixin],
  components: { OcrTabs, OcrEditableGrid, OcrPageShimmer, OcrApiErrorPanel, OcrTableWrap },
  inject: {
    ocrContextKey: { default: () => () => 0 },
  },
  data() {
    return {
      loading: true,
      loadError: null,
      activeTab: "reports",
      testTypes: ["All Tests"],
      tableOptions: [],
      reportSummary: [],
      reportFiles: [],
      mergedFiles: [],
      sourceFiles: [],
      completedFiles: [],
      reportTestType: "All Tests",
      mergedTestType: "All Tests",
      selectedReports: [],
      selectedSource: [],
      selectedCompleted: [],
      csvGrids: {},
      loadingCsv: null,
      savingCsv: null,
      uploadingCsv: false,
      merging: false,
      viewMerged: false,
      mergedConfigs: {},
      jsonPreview: {},
      uploadingFile: null,
      uploadingAll: false,
      uploadingSource: false,
      copyingCompleted: false,
      completedDialog: false,
      confirmDeleteSamples: false,
      deletingSamples: false,
      statusMessage: "",
      statusType: "info",
    };
  },
  computed: {
    folderEpoch() {
      return this.ocrContextKey();
    },
    reportSummaryColumns() {
      if (!this.reportSummary.length) return ["Report"];
      const keys = Object.keys(this.reportSummary[0]);
      return ["Report", ...keys.filter((k) => k !== "Report")];
    },
    tableTypeNames() {
      return this.tableOptions.map((t) => t.table);
    },
    visibleMergedFiles() {
      if (this.mergedTestType === "All Tests") return this.mergedFiles;
      return this.mergedFiles.filter((f) => this.mergedTableType(f) === this.mergedTestType);
    },
  },
  watch: {
    statusMessage(message) {
      if (!message) return;
      this.$nextTick(() => {
        if (this.statusMessage) this.ocrToast(this.statusMessage, this.statusType);
      });
    },
    folderEpoch() {
      this.loadOverview();
    },
    selectedReports: {
      handler(names) {
        names.forEach((report) => {
          this.filesForReport(report).forEach((file) => this.ensureCsv(file.file_name));
        });
      },
    },
    viewMerged(on) {
      if (on) {
        this.visibleMergedFiles.forEach((file) => {
          this.ensureMergedConfig(file);
          this.ensureCsv(file);
        });
      }
    },
    visibleMergedFiles(files) {
      if (!this.viewMerged) return;
      files.forEach((file) => {
        this.ensureMergedConfig(file);
        this.ensureCsv(file);
      });
    },
  },
  mounted() {
    this.loadOverview();
  },
  methods: {
    filesForReport(report) {
      return this.reportFiles
        .filter((f) => f.report === report)
        .filter((f) => this.reportTestType === "All Tests" || f.test === this.reportTestType)
        .sort((a, b) => Number(a.page) - Number(b.page));
    },
    fileHeading(file) {
      if (String(file.file_name).endsWith("Manual_Sample.csv")) {
        return `${file.test} - Manual Samples`;
      }
      return `${file.test} - Page ${file.page}`;
    },
    mergedTableType(filename) {
      return String(filename).replace(/^Merged_/, "").replace(/\.csv$/i, "");
    },
    displayNamesFor(tableType) {
      const opt = this.tableOptions.find((t) => t.table === tableType);
      return opt?.display_names?.length ? opt.display_names : [tableType];
    },
    ensureMergedConfig(file) {
      if (this.mergedConfigs[file]) return;
      const tableType = this.mergedTableType(file);
      const names = this.displayNamesFor(tableType);
      this.mergedConfigs = {
        ...this.mergedConfigs,
        [file]: { tableType, displayName: names[0] || tableType },
      };
    },
    onMergedTypeChange(file) {
      const names = this.displayNamesFor(this.mergedConfigs[file].tableType);
      this.mergedConfigs[file].displayName = names[0] || this.mergedConfigs[file].tableType;
    },
    toggleReport(name) {
      const set = new Set(this.selectedReports);
      if (set.has(name)) set.delete(name);
      else set.add(name);
      this.selectedReports = [...set];
    },
    toggleSource(name) {
      const set = new Set(this.selectedSource);
      if (set.has(name)) set.delete(name);
      else set.add(name);
      this.selectedSource = [...set];
    },
    toggleCompleted(name) {
      const set = new Set(this.selectedCompleted);
      if (set.has(name)) set.delete(name);
      else set.add(name);
      this.selectedCompleted = [...set];
    },
    async loadOverview() {
      this.loading = true;
      this.loadError = null;
      try {
        const { data } = await ocrApi.get("api/v1/test-data/overview");
        this.testTypes = data.test_types?.length ? data.test_types : ["All Tests"];
        this.tableOptions = data.table_options || [];
        this.reportSummary = data.report_summary || [];
        this.reportFiles = data.report_files || [];
        this.mergedFiles = data.merged_files || [];
        this.sourceFiles = data.source_files || [];
        this.completedFiles = data.completed_files || [];
        this.selectedReports = this.selectedReports.filter((n) =>
          this.reportSummary.some((r) => r.Report === n),
        );
      } catch (e) {
        this.loadError = formatOcrApiError(e, "Could not load test data.");
      } finally {
        this.loading = false;
      }
    },
    async ensureCsv(filename) {
      if (this.csvGrids[filename]) return;
      this.loadingCsv = filename;
      try {
        const { data } = await ocrApi.get("api/v1/test-data/csv", { params: { filename } });
        this.csvGrids = { ...this.csvGrids, [filename]: data.grid };
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not load CSV.");
        this.statusType = "error";
      } finally {
        this.loadingCsv = null;
      }
    },
    async saveCsv(filename, grid) {
      this.savingCsv = filename;
      try {
        await ocrApi.put("api/v1/test-data/csv", { filename, grid });
        this.csvGrids = { ...this.csvGrids, [filename]: grid };
        this.statusMessage = `Saved ${filename}`;
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Save failed.");
        this.statusType = "error";
      } finally {
        this.savingCsv = null;
      }
    },
    async downloadCsv(filename) {
      const res = await ocrApi.get("api/v1/test-data/csv/download", {
        params: { filename },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    async uploadCsv(event, merged) {
      const files = [...(event.target.files || [])];
      event.target.value = "";
      if (!files.length) return;
      this.uploadingCsv = true;
      try {
        const form = new FormData();
        files.forEach((f) => form.append("files", f));
        await ocrApi.post("api/v1/test-data/csv/upload", form, { params: { merged } });
        await this.loadOverview();
        this.statusMessage = "File(s) uploaded successfully.";
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "CSV upload failed.");
        this.statusType = "error";
      } finally {
        this.uploadingCsv = false;
      }
    },
    async mergeFiles() {
      this.merging = true;
      this.statusMessage = `Merging ${this.mergedTestType} files`;
      this.statusType = "info";
      try {
        const { data } = await ocrApi.post("api/v1/test-data/merge", {
          selected_type: this.mergedTestType,
        });
        this.statusMessage = (data.messages || []).join(" · ") || "Merge complete.";
        this.statusType = "success";
        this.viewMerged = true;
        this.csvGrids = {};
        await this.loadOverview();
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Merge failed.");
        this.statusType = "error";
      } finally {
        this.merging = false;
      }
    },
    async deleteMerged(filename) {
      await ocrApi.post("api/v1/test-data/csv/delete", { files: [filename] });
      this.csvGrids = { ...this.csvGrids };
      delete this.csvGrids[filename];
      await this.loadOverview();
    },
    async deleteAllMerged() {
      if (!this.mergedFiles.length) return;
      await ocrApi.post("api/v1/test-data/csv/delete", { files: this.mergedFiles });
      this.csvGrids = {};
      await this.loadOverview();
    },
    async uploadMerged(filename) {
      this.ensureMergedConfig(filename);
      const cfg = this.mergedConfigs[filename];
      this.uploadingFile = filename;
      try {
        const { data } = await ocrApi.post("api/v1/test-data/generate-json", {
          filename,
          table_type: cfg.tableType,
          display_name: cfg.displayName,
          upload: true,
        });
        this.jsonPreview = {
          ...this.jsonPreview,
          [filename]: JSON.stringify(data.json || {}, null, 2),
        };
        const up = data.upload;
        this.statusMessage = up
          ? `Uploaded ${up.uploaded}, failed ${up.failed}. ${(up.log || []).slice(0, 4).join(" ")}`
          : "JSON generated.";
        this.statusType = up?.failed ? "warning" : "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Upload to database failed.");
        this.statusType = "error";
      } finally {
        this.uploadingFile = null;
      }
    },
    async uploadAll() {
      this.uploadingAll = true;
      try {
        const { data } = await ocrApi.post("api/v1/test-data/upload-all");
        const ok = (data.results || []).filter((r) => r.ok).length;
        this.statusMessage = `Uploaded ${ok} / ${(data.results || []).length} merged files.`;
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Upload all failed.");
        this.statusType = "error";
      } finally {
        this.uploadingAll = false;
      }
    },
    async uploadSource(event) {
      const files = [...(event.target.files || [])];
      event.target.value = "";
      if (!files.length) return;
      this.uploadingSource = true;
      try {
        const form = new FormData();
        files.forEach((f) => form.append("files", f));
        await ocrApi.post("api/v1/test-data/source-files/upload", form);
        await this.loadOverview();
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Source upload failed.");
        this.statusType = "error";
      } finally {
        this.uploadingSource = false;
      }
    },
    async copyCompleted() {
      this.copyingCompleted = true;
      try {
        await ocrApi.post("api/v1/test-data/source-files/from-completed", {
          files: this.selectedCompleted,
        });
        this.completedDialog = false;
        this.selectedCompleted = [];
        await this.loadOverview();
      } finally {
        this.copyingCompleted = false;
      }
    },
    async deleteSource() {
      await ocrApi.post("api/v1/test-data/source-files/delete", { files: this.selectedSource });
      this.selectedSource = [];
      await this.loadOverview();
    },
    async deleteAllSamples() {
      this.deletingSamples = true;
      try {
        let remaining = 1;
        while (remaining > 0) {
          const { data } = await api.get("public/api/samples", {
            params: { pageNumber: 1, pageSize: 100 },
          });
          const samples = data?.data?.samples || [];
          remaining = samples.length;
          for (const sample of samples) {
            await api.delete(`public/api/samples/${sample.ID}`);
          }
          if (!samples.length) break;
        }
        this.statusMessage = "All samples deleted.";
        this.statusType = "success";
        this.confirmDeleteSamples = false;
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not delete samples.");
        this.statusType = "error";
      } finally {
        this.deletingSamples = false;
      }
    },
    goToReport(file) {
      const tenant = this.$route.params.tenantPrefix;
      const page = Number(file.page);
      const tab = page === -1 ? "samples" : "map";
      this.$router.push({
        path: `/${tenant}/ocr/reports`,
        query: {
          file: file.report,
          page: String(file.page),
          table: String(file.table),
          tab,
        },
      });
    },
  },
};
</script>
